/**
 * Analytics Plugin
 * Initializes analytics, registers providers, and installs auto-tracking listeners.
 * Also registers the v-track-ignore and v-track Vue directives globally.
 */

import type { ObjectDirective } from 'vue'
import { watch, nextTick } from 'vue'
import { defineNuxtPlugin, useRuntimeConfig, useRouter } from 'nuxt/app'
import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { useLocation } from '@common/location-layer/composables/useLocation'
import { getConfig } from '@common/core-layer/config'
import { APP_NAMES } from '@common/core-layer/stores/global.store'
import { AnalyticsEvents } from '../types'
import { BackendAnalyticsProvider } from '../providers/backend-analytics.provider'
import { analyticsService } from '../services/analytics.service'
import { getAutoTrackedClick, vTrack, vTrackIgnore } from '../directives/analytics.directives'
import { registerGoogleAnalyticsFromPlugin } from '../utils/register-google-analytics-from-plugin.utils'
import { isGoogleAnalyticsSendEnabled } from '../utils/is-google-analytics-send-enabled.utils'
import { registerGokollabCpStream, resetGokollabCpStream } from '../utils/register-gokollab-dual-stream.utils'
import { useGtag } from '#imports'

export function syncAnalyticsLocationContext(locationId: string | null | undefined): void {
  analyticsService.updateContext('locationId', locationId || undefined)
}

export default defineNuxtPlugin(nuxtApp => {
  const config = useRuntimeConfig()
  const analyticsConfig = config.public.analytics

  if (!analyticsConfig?.enabled) {
    const noopDirective = {} as ObjectDirective<HTMLElement>
    nuxtApp.vueApp.directive('track-ignore', noopDirective)
    nuxtApp.vueApp.directive('track', noopDirective)
    console.log('[Analytics] Disabled')
    return
  }

  analyticsService.initialize(analyticsConfig.debug || false)

  const appName = (config.public.appName as string) || ''
  const isGokollabApp = appName === APP_NAMES.GOKOLLAB

  const canSendAuthenticatedAnalytics = (): boolean => {
    if (isGokollabApp) return true
    return Boolean(analyticsService.getContext().contactId)
  }

  const cleanupFunctions: (() => void)[] = []

  // ===== Register Vue directives =====
  nuxtApp.vueApp.directive('track-ignore', vTrackIgnore)
  nuxtApp.vueApp.directive('track', vTrack)

  // ===== Register providers =====

  registerGoogleAnalyticsFromPlugin(
    {
      ...analyticsConfig,
      appEnv: config.public.appEnv as string,
      appName,
    },
    useGtag
  )

  if (analyticsConfig.providers?.backendAnalytics?.enabled) {
    analyticsService.registerProvider('backendAnalytics', new BackendAnalyticsProvider(analyticsConfig.debug))
  }

  // ===== Auto-track: User Identification =====
  if (analyticsConfig.autoTrack?.userIdentification !== false) {
    try {
      const { store: currentUserStore } = useCurrentUser()
      const { gtag } = useGtag()

      const stopUserWatch = watch(
        () => currentUserStore.profile,
        profile => {
          if (profile) {
            const contactId = (profile as { contactId?: string }).contactId

            if (!contactId) {
              if (!isGokollabApp) {
                console.warn('[Analytics] Cannot identify: profile has no contactId')
              }
              return
            }

            const canSendToGa = isGoogleAnalyticsSendEnabled(config.public.appEnv as string)

            analyticsService.identify(contactId, {
              appName: config.public.appName || 'Unknown',
            })

            if (canSendToGa && appName === APP_NAMES.GOKOLLAB) {
              registerGokollabCpStream(gtag, getConfig().GOOGLE_ANALYTICS.GOKOLLAB_CP_STREAM, contactId, canSendToGa)
            }
          } else {
            analyticsService.updateContext('contactId', undefined)
            resetGokollabCpStream()
          }
        },
        { immediate: true }
      )

      cleanupFunctions.push(stopUserWatch)
    } catch (error) {
      console.warn('[Analytics] User tracking not available:', error)
    }
  }

  // ===== Auto-track: Location Context =====
  try {
    const { locationId: locationIdRef } = useLocation()

    const stopLocationWatch = watch(
      () => locationIdRef.value,
      locationId => {
        syncAnalyticsLocationContext(locationId)
      },
      { immediate: true }
    )

    cleanupFunctions.push(stopLocationWatch)
  } catch (error) {
    console.warn('[Analytics] Location tracking not available:', error)
  }

  // ===== Auto-track: Clicks (via element id convention) =====
  if (analyticsConfig.autoTrack?.clicks !== false) {
    const handleClick = (event: MouseEvent) => {
      if (!canSendAuthenticatedAnalytics()) return
      const target = event.target as HTMLElement
      const clickTracking = getAutoTrackedClick(target)
      if (!clickTracking) return
      analyticsService.track(clickTracking.event, clickTracking.properties)
    }

    document.addEventListener('click', handleClick, { capture: true, passive: true })
    cleanupFunctions.push(() => document.removeEventListener('click', handleClick, { capture: true }))
  }

  // ===== Auto-track: Page Views & Route Context =====
  if (analyticsConfig.autoTrack?.pageViews !== false) {
    const router = useRouter()

    const removeAfterEach = router.afterEach((to, from) => {
      // Route template (e.g. /courses/:productId/lessons) — groups all parameterised
      // variations into one funnel step.
      const slugifiedRoute = to.matched.at(-1)?.path ?? to.path
      const fromSlugifiedRoute = from.matched.at(-1)?.path ?? from.path

      analyticsService.updateContext('pagePath', to.path)
      analyticsService.updateContext('routeName', to.name)
      analyticsService.updateContext('slugifiedRoute', slugifiedRoute)

      nextTick(() => {
        if (!canSendAuthenticatedAnalytics()) return

        let pageTitle = document.title
        try {
          if (!pageTitle || pageTitle.trim() === '') {
            pageTitle = to.name
              ? String(to.name)
                  .split('-')
                  .filter(part => !part.includes('Slug') && !part.includes('['))
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
              : to.path
          }
        } catch {
          pageTitle = to.path
        }

        analyticsService.track(AnalyticsEvents.PAGE_VIEW, {
          pageLocation: window.location.href,
          pageTitle,
          route: to.path,
          slugifiedRoute,
          fromPagePath: from.path,
          fromRouteName: from.name as string,
          fromSlugifiedRoute,
          fromPageLocation: from.fullPath ? window.location.origin + from.fullPath : undefined,
        })
      })
    })

    cleanupFunctions.push(removeAfterEach)
  }

  // ===== Auto-track: Errors =====
  if (analyticsConfig.autoTrack?.errors !== false) {
    const handleError = (event: ErrorEvent) => {
      if (!canSendAuthenticatedAnalytics()) return
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: event.message,
        errorType: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!canSendAuthenticatedAnalytics()) return
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: (event.reason as Error)?.message || String(event.reason),
        errorType: 'unhandled_promise_rejection',
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    cleanupFunctions.push(() => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    })
  }

  // ===== Cleanup =====
  let isCleanedUp = false

  const performCleanup = () => {
    if (isCleanedUp) return
    cleanupFunctions.forEach(fn => fn())
    isCleanedUp = true
  }

  // beforeunload paired with its removeEventListener in cleanup
  window.addEventListener('beforeunload', performCleanup)
  cleanupFunctions.push(() => window.removeEventListener('beforeunload', performCleanup))

  console.log('[Analytics] Initialized successfully')
})
